import { defaultBrandTheme } from '../../../design-system';
import type {
  CompanyCreateInput,
  CompanyLegalProfile,
  EmployeeNumberSettings,
  OrganizationSettings,
  ReferenceJobFamily,
  ReferenceJobLevel,
  ReferenceJobProfile,
  ReferenceJobType,
  ReferenceLocation,
  ReferenceWorkSpace,
} from '../model/types';
export const defaultEmployeeNumberSettings: EmployeeNumberSettings = {
  prefix: 'P',
  dateBasis: 'HIRE_DATE',
  dateFormat: 'YYMM',
  sequenceLength: 3,
  resetCycle: 'MONTHLY',
  sequenceScope: 'COMPANY',
  reuseFormerWorkerId: false,
  version: 1,
};
export const defaultOrganizationSettings: OrganizationSettings = {
  displayName: '한결',
  brandTheme: defaultBrandTheme,
  version: 1,
};
export const companyLegalFixtures: CompanyLegalProfile[] = [
  {
    companyId: 'company-hangyeol',
    companyCode: 'HANGYEOL',
    descriptor: '한결',
    isPrimary: true,
    legalName: '주식회사 한결',
    mainPhoneNumber: '02-1234-5678',
    foundedOn: '2024-01-01',
    registeredAddress: '서울특별시 성동구 왕십리로 83-21, 4층(성수동1가)',
    businessRegistrationNumber: '000-00-00000',
    corporateRegistrationNumber: '000000-0000000',
    representativeTitle: '대표이사',
    representativeName: '박재욱',
    sealConfigured: true,
    version: 1,
    updatedAt: '2026-08-25T09:00:00Z',
  },
  {
    companyId: 'company-hangyeol-labs',
    companyCode: 'HANGYEOL-LABS',
    descriptor: '한결랩스',
    isPrimary: false,
    legalName: '주식회사 한결랩스',
    mainPhoneNumber: '',
    foundedOn: '',
    registeredAddress: '서울특별시 성동구 성수이로 12',
    businessRegistrationNumber: '000-00-00015',
    corporateRegistrationNumber: '000000-0000018',
    representativeTitle: '대표이사',
    representativeName: '김에이',
    sealConfigured: false,
    version: 1,
    updatedAt: '2026-08-25T09:00:00Z',
  },
];
export const emptyCompanyCreateInput: CompanyCreateInput = {
  companyCode: '',
  descriptor: '',
  countryCode: 'KR',
  legalName: '',
  mainPhoneNumber: '',
  foundedOn: '',
  registeredAddress: '',
  businessRegistrationNumber: '',
  corporateRegistrationNumber: '',
  representativeTitle: '대표이사',
  representativeName: '',
};
export const referenceLocationFixtures: ReferenceLocation[] = [
  {
    id: 'location-seoul',
    code: 'SEOUL-HQ',
    descriptor: '서울 본사',
    locationType: 'OFFICE',
    countryCode: 'KR',
    timeZone: 'Asia/Seoul',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'location-gyeongbuk',
    code: 'GYEONGBUK',
    descriptor: '경북 지점',
    locationType: 'OFFICE',
    countryCode: 'KR',
    timeZone: 'Asia/Seoul',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
export const referenceJobFamilyFixtures: ReferenceJobFamily[] = [
  {
    id: 'job-family-engineering',
    code: 'ENGINEERING',
    descriptor: '엔지니어링',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-family-people',
    code: 'PEOPLE',
    descriptor: '피플',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-family-operations',
    code: 'OPERATIONS',
    descriptor: '오퍼레이션',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
export const referenceJobProfileFixtures: ReferenceJobProfile[] = [
  {
    id: 'job-profile-engineer',
    code: 'SOFTWARE-ENGINEER',
    descriptor: 'Software Engineer',
    jobFamily: {
      id: 'job-family-engineering',
      code: 'ENGINEERING',
      descriptor: '엔지니어링',
    },
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-profile-people-partner',
    code: 'PEOPLE-PARTNER',
    descriptor: 'People Partner',
    jobFamily: {
      id: 'job-family-people',
      code: 'PEOPLE',
      descriptor: '피플',
    },
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
export const referenceJobLevelFixtures: ReferenceJobLevel[] = [
  {
    id: 'job-level-l1',
    code: 'L1',
    descriptor: 'L1',
    sortOrder: 10,
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-level-l2',
    code: 'L2',
    descriptor: 'L2',
    sortOrder: 20,
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-level-l3',
    code: 'L3',
    descriptor: 'L3',
    sortOrder: 30,
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
export const referenceJobTypeFixtures: ReferenceJobType[] = [
  {
    id: 'job-type-regular',
    code: 'REGULAR',
    descriptor: '정규직',
    workerType: 'EMPLOYEE',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-type-fixed-term',
    code: 'FIXED-TERM',
    descriptor: '기간제',
    workerType: 'EMPLOYEE',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'job-type-contractor',
    code: 'CONTRACTOR',
    descriptor: '용역',
    workerType: 'CONTINGENT_WORKER',
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
export const referenceWorkSpaceFixtures: ReferenceWorkSpace[] = [
  {
    id: 'work-space-seoul-engineering',
    code: 'SEOUL-4F-ENGINEERING',
    descriptor: '4층 Engineering Zone',
    location: {
      id: 'location-seoul',
      code: 'SEOUL-HQ',
      descriptor: '서울 본사',
    },
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
  {
    id: 'work-space-seoul-product',
    code: 'SEOUL-5F-PRODUCT',
    descriptor: '5층 Product Zone',
    location: {
      id: 'location-seoul',
      code: 'SEOUL-HQ',
      descriptor: '서울 본사',
    },
    status: 'ACTIVE',
    effectiveFrom: '2024-01-01',
    version: 1,
  },
];
